import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

export const DAY_MS = 86400000;
export const YEAR_MS = 365 * DAY_MS;

export const DATE_PRESETS = [
  ['all', 'All'],
  ['7d', 'Last 7d'],
  ['30d', 'Last 30d'],
  ['thisyear', 'This yr'],
  ['last', 'Last yr'],
  ['first', 'First yr'],
];

// 7d/30d/last/first all anchor to the data's own max (most recent session),
// not real wall-clock "now" — consistent with the pre-existing last/first math.
function windowFor(key, min, max) {
  switch (key) {
    case '7d':  return [Math.max(min, max - 7 * DAY_MS), max];
    case '30d': return [Math.max(min, max - 30 * DAY_MS), max];
    case 'thisyear': {
      // Day-aligned, because callers drive the slider with step=DAY_MS and the
      // native step grid runs from the track's own min: a local-midnight start
      // leaves `max` off-grid and unreachable by the right handle.
      const soy = Math.floor(new Date(new Date().getFullYear(), 0, 1).getTime() / DAY_MS) * DAY_MS;
      return [Math.min(Math.max(min, soy), max), max];
    }
    case 'last':  return [Math.max(min, max - YEAR_MS), max];
    case 'first': return [min, Math.min(max, min + YEAR_MS)];
    default:      return [min, max]; // 'all'
  }
}

// Dual-handle date-range state shared by the Upload and Channel Stats pages.
// `bounds` is {min,max} in ms (or null while unknown). Returns a concrete
// `range` (falls back to full bounds once known), a raw `setRange` for
// manual dragging, `applyPreset` for the quick buttons, `activePreset` (the
// key of whichever preset button should read as selected), and `reset`.
//
// A preset click also zooms the slider's own track to that preset's window
// (`sliderMin`/`sliderMax`) so fine-tuning afterward has real drag resolution
// instead of a handle-width sliver on a track still spanning the full dataset.
// Manual dragging never re-zooms the track — only a preset click does. It does
// clear `activePreset`, though: once the user drags a handle the selection no
// longer matches any preset exactly, so nothing should still read as active.
export default function useDateRangePresets(bounds) {
  const min = bounds?.min ?? null;
  const max = bounds?.max ?? null;

  const [rawRange, setRawRange] = useState(null); // null = untouched/full
  const [domain, setDomain] = useState(null);     // null = zoomed out to full bounds
  const [activePreset, setActivePreset] = useState(null); // key of the highlighted preset button
  const prevRef = useRef(null); // previous {min,max} primitives

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = (min == null || max == null) ? null : { min, max };
    setDomain(d => d === null ? d : null); // fresh bounds context -> zoom back out
    setActivePreset(null);

    if (min == null || max == null) {
      setRawRange(r => r === null ? r : null);
      return;
    }
    setRawRange(r => {
      if (!r) return [min, max];
      const wasFullRange = prev && r[0] === prev.min && r[1] === prev.max;
      const target = wasFullRange
        ? [min, max]
        : [Math.max(min, Math.min(r[0], max)), Math.min(max, Math.max(r[1], min))];
      return (target[0] === r[0] && target[1] === r[1]) ? r : target;
    });
  }, [min, max]);

  const range = useMemo(() => rawRange ?? (min == null ? null : [min, max]),
    [rawRange, min, max]);

  const setRange = useCallback((v) => { setActivePreset(null); setRawRange(v); }, []);

  const applyPreset = useCallback((key) => {
    if (min == null || max == null) return;
    const w = windowFor(key, min, max);
    setRawRange(w);
    // Never zoom onto a zero-width window — 'thisyear' collapses to [max, max]
    // when nothing was driven this calendar year, and a track whose min equals
    // its max renders both handles immovable. The range still collapses (that
    // part is the honest answer), but the track stays draggable.
    setDomain(key === 'all' || w[1] <= w[0] ? null : w);
    setActivePreset(key);
  }, [min, max]);

  const reset = useCallback(() => { setRawRange(null); setDomain(null); setActivePreset(null); }, []);

  return {
    range, setRange, applyPreset, reset, activePreset,
    sliderMin: domain ? domain[0] : min,
    sliderMax: domain ? domain[1] : max,
  };
}
